/**
 * SDK Lanes Initialization
 *
 * This module provides initialization for sdk-lanes, wrapping sdk-core's init
 * and adding Google API key configuration for geo providers.
 */
import {
  init as coreInit,
  createFetchExecutor,
  type GqlExecutor,
  type ExecutorConfig,
  type Logger,
} from '@hopdrive/sdk-core'
import { createGoogleProviders } from './geo/index.js'
import type { GeocodingProvider } from './geo/types/geocoding.js'
import type { RoutingProvider } from './geo/types/routing.js'
import type { SuggestionProvider } from './geo/types/suggestion.js'

// =============================================================================
// Types
// =============================================================================

/**
 * Configuration for sdk-lanes initialization.
 * Provide either `executor` (pre-created) or `executorConfig` (to create one), but not both.
 */
export interface LanesInitConfig {
  /** Pre-created GraphQL executor. Mutually exclusive with executorConfig. */
  executor?: GqlExecutor
  /** Config to create a GraphQL executor. Mutually exclusive with executor. */
  executorConfig?: ExecutorConfig
  /** Google API key for geocoding and routing operations */
  googleApiKey: string
  /** Optional logger for SDK observability. Defaults to no-op. */
  logger?: Logger
}

/**
 * Geo providers created during initialization
 */
export interface GeoProviders {
  geocoding: GeocodingProvider
  routing: RoutingProvider
  suggestion: SuggestionProvider
}

// =============================================================================
// Module State
// =============================================================================

let defaultGeoProviders: GeoProviders | null = null
let googleApiKey: string | null = null

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize sdk-lanes with executor and Google API key.
 *
 * This wraps sdk-core's init and creates default geo providers for
 * location resolution and geocoding.
 *
 * @example Using executorConfig (creates executor internally)
 * ```typescript
 * import { init, resolveLocation } from '@hopdrive/sdk-lanes'
 *
 * init({
 *   executorConfig: {
 *     url: graphqlUrl,        // From your app's configuration
 *     adminSecret: adminSecret
 *   },
 *   googleApiKey: googleApiKey // From your app's configuration
 * })
 *
 * const result = await resolveLocation({
 *   address: '123 Main St',
 *   customerId: 1
 * })
 * ```
 *
 * @example Using pre-created executor
 * ```typescript
 * import { init } from '@hopdrive/sdk-lanes'
 * import { createFetchExecutor } from '@hopdrive/sdk-core'
 *
 * const executor = createFetchExecutor({
 *   url: graphqlUrl,        // From your app's configuration
 *   adminSecret: adminSecret
 * })
 *
 * init({
 *   executor,
 *   googleApiKey: googleApiKey // From your app's configuration
 * })
 * ```
 */
export function init(config: LanesInitConfig): void {
  // Validate lanes-specific requirements
  if (!config.googleApiKey) {
    throw new Error(
      'sdk-lanes requires googleApiKey. Provide it in init({ googleApiKey: "..." })'
    )
  }

  // Validate executor configuration - exactly one must be provided
  if (config.executor && config.executorConfig) {
    throw new Error(
      'Provide either "executor" or "executorConfig", not both.'
    )
  }
  if (!config.executor && !config.executorConfig) {
    throw new Error(
      'Must provide either "executor" (pre-created) or "executorConfig" (to create one).'
    )
  }

  // Resolve executor
  const executor = config.executor ?? createFetchExecutor(config.executorConfig!)

  // Call core's init to set up the executor (and optional logger)
  coreInit({ executor, logger: config.logger })

  // Set up lanes-specific state
  googleApiKey = config.googleApiKey
  const providers = createGoogleProviders(config.googleApiKey)
  defaultGeoProviders = {
    geocoding: providers.geocoding,
    routing: providers.routing,
    suggestion: providers.suggestion,
  }
}

// =============================================================================
// Geo Provider Access
// =============================================================================

/**
 * Get the default geo providers created during initialization.
 * Throws if init() has not been called.
 */
export function getDefaultGeoProviders(): GeoProviders {
  if (!defaultGeoProviders) {
    throw new Error(
      'sdk-lanes not initialized. Call init() first with executor and googleApiKey.'
    )
  }
  return defaultGeoProviders
}

/**
 * Get the Google API key configured during initialization.
 * Throws if init() has not been called.
 */
export function getGoogleApiKey(): string {
  if (!googleApiKey) {
    throw new Error(
      'sdk-lanes not initialized. Call init() first with executor and googleApiKey.'
    )
  }
  return googleApiKey
}

/**
 * Check if sdk-lanes has been initialized.
 */
export function isInitialized(): boolean {
  return defaultGeoProviders !== null
}

// =============================================================================
// Reset (for testing)
// =============================================================================

/**
 * Reset lanes-specific configuration (useful for testing).
 * Note: This does NOT reset the core executor - use resetDefaultExecutor() for that.
 */
export function resetLanesConfig(): void {
  defaultGeoProviders = null
  googleApiKey = null
}

// =============================================================================
// Re-exports from sdk-core
// =============================================================================

export {
  createFetchExecutor,
  resolveExecutor,
  resetDefaultExecutor,
  getLogger,
  type GqlExecutor,
  type ExecutorConfig,
  type Logger,
} from '@hopdrive/sdk-core'
